export const TICKET_TYPES = {
    support: 'support',
    featureRequest: 'feature_request',
};

export const TICKET_TYPE_LABELS = {
    support: 'Support',
    feature_request: 'Feature request',
};

export const ASSOCIATED_OBJECT_TYPES = {
    design: 'design',
    quote: 'quote',
    invoice: 'invoice',
    upload: 'upload',
};

export const ASSOCIATED_OBJECT_LABELS = {
    design: 'Design',
    quote: 'Quote',
    invoice: 'Invoice',
    upload: 'Upload',
};

export const ticketTypeLabel = (ticketType) =>
    TICKET_TYPE_LABELS[ticketType] || ticketType || 'Ticket';

export const isClosedTicketStatus = (status) => {
    const value = String(status || '').trim().toLowerCase();
    return value === 'closed' || value === 'complete' || value === 'completed' || value === 'done';
};

export const ticketStatusChipClass = (status) =>
    isClosedTicketStatus(status) ? 'status-chip--success' : 'status-chip--progress';

export const ticketResponseText = (ticket) => {
    const value = ticket?.response;
    if (value == null) return '';
    return String(value).trim();
};

export const ticketTitle = (ticket) => {
    const title = String(ticket?.title || '').trim();
    if (title) return title;
    const description = String(ticket?.description || '').replace(/\s+/g, ' ').trim();
    if (!description) return 'Untitled';
    if (description.length <= 80) return description;
    return `${description.slice(0, 80).trimEnd()}…`;
};

export const formatTicketDateTime = (value) => {
    if (value == null || value === '') return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
};

export const associatedObjectKey = (object) => {
    const type = object?.type || object?.object_type || '';
    const id = object?.id ?? object?.object_id;
    return `${type}:${id}`;
};

export const formatAssociatedObject = (object) => {
    const type = object?.type || object?.object_type || '';
    const id = object?.id ?? object?.object_id;
    const typeLabel = ASSOCIATED_OBJECT_LABELS[type] || type || 'Object';
    return `${typeLabel} ${id}`;
};

export const associatedObjectHref = (object, projectId) => {
    const type = object?.type || object?.object_type;
    const id = object?.id ?? object?.object_id;
    if (type === 'design' && id != null) return `/view-design/${id}`;
    if (!projectId) return null;
    if (type === 'quote') return `/project/${projectId}?tab=quotes`;
    if (type === 'invoice') return `/project/${projectId}?tab=invoices`;
    if (type === 'upload') return `/project/${projectId}?tab=files`;
    return null;
};

export const projectPickerLabel = (project) => {
    if (!project) return '';
    const name = project.project_name || project.name || 'Untitled project';
    const company = project.company_name ? ` — ${project.company_name}` : '';
    return `${name}${company} (#${project.id})`;
};

export const createdByLabel = (createdBy, currentUser) => {
    if (createdBy == null || createdBy === '') return '—';
    if (currentUser?.id === createdBy) {
        const name = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ');
        if (name) return `${name} (you)`;
        if (currentUser.email) return `${currentUser.email} (you)`;
        return `You (#${createdBy})`;
    }
    return `User #${createdBy}`;
};

export const ticketApiError = (result, fallback = 'Something went wrong') =>
    result?.msg || result?.message || fallback;

export const isPositiveInteger = (value) => {
    const number = Number(value);
    return Number.isInteger(number) && number > 0;
};

export const canSubmitTicket = ({ ticketType, description, projectId, title }) => {
    if (![TICKET_TYPES.support, TICKET_TYPES.featureRequest].includes(ticketType)) {
        return false;
    }
    if (!String(description || '').trim()) return false;
    if (String(title || '').length > 500) return false;
    if (ticketType === TICKET_TYPES.support && !isPositiveInteger(projectId)) {
        return false;
    }
    return true;
};

export const buildTicketPayload = ({
    ticketType,
    description,
    projectId,
    title,
    associatedObjects = [],
}) => {
    const payload = {
        ticket_type: ticketType,
        description: String(description || '').trim(),
    };

    const trimmedTitle = String(title || '').trim();
    if (trimmedTitle) {
        payload.title = trimmedTitle.slice(0, 500);
    }

    if (isPositiveInteger(projectId)) {
        payload.project_id = Number(projectId);
    }

    const objects = (associatedObjects || [])
        .map((object) => ({
            type: String(object?.type || object?.object_type || '').trim(),
            id: Number(object?.id ?? object?.object_id),
        }))
        .filter((object) => object.type && Number.isInteger(object.id));

    if (objects.length > 0 && payload.project_id) {
        payload.associated_objects = objects;
    }

    return payload;
};
